import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Loader2 } from 'lucide-react';
import { useTranslation } from '@/components/i18n/TranslationProvider';
import { authenticatedFetch } from '@/utils/authenticatedApiClient.js';

const formatEffectiveDate = (value, language) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(language === 'it' ? 'it-IT' : 'en-GB', {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone: 'Europe/Rome',
  }).format(date);
};

export default function LegalAccountNotice({ userId }) {
  const { currentLanguage, t } = useTranslation();
  const [notice, setNotice] = useState(null);
  const [status, setStatus] = useState('idle');
  const [acknowledgementError, setAcknowledgementError] = useState(false);

  useEffect(() => {
    if (!userId) {
      setNotice(null);
      setStatus('idle');
      return undefined;
    }

    const controller = new AbortController();
    const loadNotice = async () => {
      try {
        const response = await authenticatedFetch('/api/legal/account-notice', {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error('legal_notice_unavailable');
        if (!controller.signal.aborted) {
          setNotice(payload?.active ? payload.notice : null);
          setStatus('ready');
        }
      } catch (error) {
        if (error?.name !== 'AbortError' && !controller.signal.aborted) {
          setNotice(null);
          setStatus('unavailable');
        }
      }
    };

    setStatus('loading');
    loadNotice();
    return () => controller.abort();
  }, [userId]);

  const effectiveDate = useMemo(
    () => formatEffectiveDate(notice?.effectiveAt, currentLanguage),
    [currentLanguage, notice?.effectiveAt],
  );

  const acknowledge = async () => {
    if (status === 'acknowledging') return;
    setAcknowledgementError(false);
    setStatus('acknowledging');
    try {
      const response = await authenticatedFetch('/api/legal/account-notice', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'acknowledge' }),
      });
      if (!response.ok) throw new Error('legal_notice_acknowledgement_failed');
      setNotice(null);
      setStatus('ready');
    } catch {
      setAcknowledgementError(true);
      setStatus('ready');
    }
  };

  if (!userId || status === 'loading' || status === 'unavailable' || !notice) {
    return null;
  }

  return (
    <section
      className="mx-auto mt-3 w-[calc(100%-2rem)] max-w-7xl rounded-lg border border-cyan-300/20 bg-slate-950/80 px-3 py-2.5 text-slate-100 shadow-sm sm:flex sm:items-center sm:justify-between sm:gap-4 sm:px-4"
      aria-labelledby="legal-account-notice-title"
    >
      <div className="flex min-w-0 items-start gap-2.5">
        <FileText className="mt-0.5 h-4 w-4 flex-none text-cyan-300" aria-hidden="true" />
        <div className="min-w-0 text-xs leading-5 text-slate-300">
          <p id="legal-account-notice-title" className="font-semibold text-slate-100">
            {t('legal.account_notice.title')}
          </p>
          <p>
            {t('legal.account_notice.description', {
              version: notice.policyVersion,
              date: effectiveDate || t('legal.account_notice.date_pending'),
            })}
          </p>
          {acknowledgementError ? (
            <p className="text-amber-200" role="alert">
              {t('legal.account_notice.acknowledgement_error')}
            </p>
          ) : null}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 pl-6 text-xs sm:mt-0 sm:flex-none sm:pl-0">
        <Link
          to={notice.legalPath || '/legal'}
          className="min-h-11 content-center font-semibold text-cyan-200 underline-offset-4 hover:text-cyan-50 hover:underline"
        >
          {t('legal.account_notice.read')}
        </Link>
        <button
          type="button"
          onClick={acknowledge}
          disabled={status === 'acknowledging'}
          className="inline-flex min-h-11 items-center gap-1.5 font-semibold text-slate-300 underline-offset-4 hover:text-white hover:underline disabled:cursor-wait disabled:opacity-70"
        >
          {status === 'acknowledging' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : null}
          {t('legal.account_notice.acknowledge')}
        </button>
      </div>
    </section>
  );
}
